import {
  getConflictingIds,
  getModifiedFieldIds,
  getModifiedFieldKeys,
  hasConflictingChanges,
} from './getModifiedFieldIds';
import { mergeSchemaContents } from './mergeSchemaContents';

export type SchemaAutoMergeOptions = {
  originalContent: unknown;
  localContent: unknown;
  remoteContent: unknown;
  hasExternalChanges: boolean;
  hasLocalChanges: boolean;
};

export type SchemaAutoMergeResult = {
  canAutoMerge: boolean;
  mergedContent: unknown;
  conflictingIds: Set<string>;
  localModifiedIds: Set<string>;
  remoteModifiedKeys: Map<string, Set<string>>;
};

const EMPTY_RESULT: SchemaAutoMergeResult = {
  canAutoMerge: false,
  mergedContent: null,
  conflictingIds: new Set(),
  localModifiedIds: new Set(),
  remoteModifiedKeys: new Map(),
};

export const getSchemaAutoMerge = ({
  originalContent,
  localContent,
  remoteContent,
  hasExternalChanges,
  hasLocalChanges,
}: SchemaAutoMergeOptions): SchemaAutoMergeResult => {
  if (!hasExternalChanges) {
    return EMPTY_RESULT;
  }

  if (!originalContent || !remoteContent) {
    return EMPTY_RESULT;
  }

  if (!hasLocalChanges) {
    const remoteModifiedKeys = getModifiedFieldKeys(
      originalContent,
      remoteContent
    );
    return {
      canAutoMerge: true,
      mergedContent: remoteContent,
      conflictingIds: new Set(),
      localModifiedIds: new Set(),
      remoteModifiedKeys,
    };
  }

  if (!localContent) {
    return EMPTY_RESULT;
  }

  const localModifiedIds = getModifiedFieldIds(originalContent, localContent);
  const remoteModifiedIds = getModifiedFieldIds(originalContent, remoteContent);
  const remoteModifiedKeys = getModifiedFieldKeys(
    originalContent,
    remoteContent
  );

  if (localModifiedIds.size === 0 || remoteModifiedIds.size === 0) {
    return {
      canAutoMerge: true,
      mergedContent:
        remoteModifiedIds.size === 0 ? localContent : remoteContent,
      conflictingIds: new Set(),
      localModifiedIds,
      remoteModifiedKeys,
    };
  }

  const conflictingIds = getConflictingIds(localModifiedIds, remoteModifiedIds);
  const hasConflicts = hasConflictingChanges(
    localModifiedIds,
    remoteModifiedIds
  );

  if (hasConflicts) {
    return {
      canAutoMerge: false,
      mergedContent: null,
      conflictingIds,
      localModifiedIds,
      remoteModifiedKeys,
    };
  }

  const mergedContent = mergeSchemaContents(
    originalContent,
    localContent,
    remoteContent,
    localModifiedIds
  );

  return {
    canAutoMerge: true,
    mergedContent,
    conflictingIds: new Set(),
    localModifiedIds,
    remoteModifiedKeys,
  };
};
